import { Button, Table, TablePaginationConfig, TableProps } from 'antd';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	GetCandidatesResponse,
	NPHHSortingFieldName,
	NPHHSortingRequest,
	PagingConfig,
	PreworkDashboardDetail,
	PreworkDashboardFilter,
	PreworkCandidateFilter,
	SortingOrder,
	PreworkCandidateSummary,
} from '../../shared/PreWorkServiceTypes';
import { CandidateTableColumnsPreWork } from './ColumnsPrework';
import { FilterCandidatesPreWork } from './CreateGroupPage/FilterCandidates';
import {
	DEFAULT_PAGE_SIZE,
	MAX_CANDIDATES_FOR_GROUP,
} from '../../shared/Constants';
import { useBatchId } from '../../hooks/useBatchId';
import { PREWORK_EDIT_GROUP, PREWORKGROUPS } from '../../shared/Routes';
import { useCreateGroupPrework } from './CreateGroupPage/useCreateGroup';
import { usePreworkGroupId } from '../../hooks/usePreworkGroupId';
import { useEditGroupPreWork } from './EditGroupPage/useEditGroup';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import isArray from 'lodash/isArray';
import { useNavigate } from 'react-router-dom';
import { PreworkDetailCardsList } from './PreworkDetailCardList';
import {
	checkIfDateIsSameOrBefore,
	hasPrivilege,
	tablePaginationConfiguration,
} from '../../shared/Utils';
import { CandidatesBatchState } from './CreateGroupPage/CandidatesList.store';
import { useSnapshot } from 'valtio';
import useUserSession from '../../hooks/useUserSession';
import { PrivilegeCodes } from '../../shared/RoleConstants';

export const CandidatesTablePrework = ({
	data: { candidates, pagination },
	onCandidatesPageChange,
	candidatePageConfig,
	candidatesFilter,
	onCandidateFilterChange,
	isMentorsTable = false,
	onCreateGroupClick,
	onAssignMentorClick,
	existingCandidatesIdsForAddMore,
	isDashboardTable = false,
	dashboardDetail,
	onDashboardDetailFilterChange,
	activeTabKey,
	selectedBatchId,
	viewCandidateMentor,
}: {
	data: GetCandidatesResponse;
	onCandidatesPageChange: (page: number, pageSize: number) => void;
	candidatePageConfig: PagingConfig;
	candidatesFilter: PreworkCandidateFilter;
	onCandidateFilterChange: (value: PreworkCandidateFilter) => void;
	isMentorsTable?: boolean;
	onCreateGroupClick?: (candidatesIds: Array<number>) => void;
	onAssignMentorClick?: (mentorId: number) => void;
	existingCandidatesIdsForAddMore?: Array<number>;
	isDashboardTable?: boolean;
	dashboardDetail?: PreworkDashboardDetail;
	onDashboardDetailFilterChange?: (value: PreworkCandidateFilter) => void;
	activeTabKey?: string | null;
	selectedBatchId: number | undefined;
	viewCandidateMentor?: boolean;
}) => {
	const existingCandidatesCount =
		existingCandidatesIdsForAddMore && existingCandidatesIdsForAddMore.length;
	const [selectedCandidates, setSelectedCandidates] = useState<React.Key[]>([]);
	const [isResultPublished, setIsResultPublished] = useState<boolean>(false);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { navigateToRoute } = useBatchId();
	const { setActiveTabKey } = useCreateGroupPrework();
	const { preworkGroupId, navigateWithPreworkGroupId } = usePreworkGroupId();
	const { updateGroup } = useEditGroupPreWork();
	const tablePaginationConfig = tablePaginationConfiguration(
		candidatePageConfig,
		pagination,
		onCandidatesPageChange
	);
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);

	const { loggedInUserPrivileges } = useUserSession();

	const showCreateGroup = hasPrivilege(
		PrivilegeCodes.CREATE_PREWORK_GROUP,
		loggedInUserPrivileges
	);

	const showAddToGroup = hasPrivilege(
		PrivilegeCodes.UPDATE_PREWORK_GROUP,
		loggedInUserPrivileges
	);

	const showPreworkCandidates = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_CANDIDATES,
		loggedInUserPrivileges
	);

	useEffect(() => {
		onCandidatesPageChange(1, 10);
	}, [onCandidatesPageChange]);

	useEffect(() => {
		setSelectedCandidates([]);
	}, [selectedBatchId]);

	useEffect(() => {
		const result = checkIfDateIsSameOrBefore(
			candidateBatchSnap?.selectedBatch?.snpResultDateForCandidate
		);
		setIsResultPublished(result);
	}, [candidateBatchSnap?.selectedBatch]);

	const onSelectChange = (newSelectedCandidateKeys: React.Key[]) => {
		setSelectedCandidates(newSelectedCandidateKeys);
	};

	const hasSelected =
		selectedCandidates.length > 0 &&
		(isMentorsTable
			? selectedCandidates.length === 1
			: selectedCandidates.length <= 5);

	const isSelectionDisabled =
		selectedCandidates.length >=
		(isMentorsTable
			? 1
			: existingCandidatesCount
			? MAX_CANDIDATES_FOR_GROUP - existingCandidatesCount
			: MAX_CANDIDATES_FOR_GROUP);

	const candidateSelection = {
		selectedCandidates,
		onChange: onSelectChange,
		preserveSelectedRowKeys: true,
		getCheckboxProps: (record: PreworkCandidateSummary) => ({
			disabled:
				isSelectionDisabled &&
				!selectedCandidates.includes(record?.applicationId as React.Key),
		}),
	};

	const numberOfCandidates = useCallback((): string => {
		const noOfCandidates = selectedCandidates.length;
		if (hasSelected && noOfCandidates === 1) {
			return `${t('selectedCandidate', { noOfCandidates })}`;
		} else if (hasSelected && noOfCandidates > 1) {
			return `${t('selectedCandidates', { noOfCandidates })}`;
		} else {
			return '';
		}
	}, [hasSelected, selectedCandidates, t]);

	const handleCreateGroupClick = useCallback(() => {
		if (onCreateGroupClick) {
			onCreateGroupClick(selectedCandidates as Array<number>);
			setSelectedCandidates([]);
		}
	}, [onCreateGroupClick, selectedCandidates]);

	const handleAssignMentorClick = useCallback(() => {
		if (onAssignMentorClick) {
			onAssignMentorClick(selectedCandidates[0] as number);
		}
	}, [onAssignMentorClick, selectedCandidates]);

	const handleAddToGroupClick = useCallback(() => {
		const candidateIdsToBeUpdated = [
			...(existingCandidatesIdsForAddMore as Array<number>),
			...selectedCandidates,
		];
		updateGroup(
			candidateIdsToBeUpdated as Array<number>,
			'areYouSureToAddGroup',
			true
		);
	}, [existingCandidatesIdsForAddMore, selectedCandidates, updateGroup]);

	const handleBackInAddMoreClick = useCallback(() => {
		if (preworkGroupId) {
			navigateWithPreworkGroupId(PREWORK_EDIT_GROUP, preworkGroupId);
		}
	}, [preworkGroupId, navigateWithPreworkGroupId]);

	const handleBackClick = useCallback(() => {
		if (isMentorsTable) {
			navigate(-1);
		} else {
			setActiveTabKey('2');
			navigateToRoute(PREWORKGROUPS);
		}
	}, [navigateToRoute, setActiveTabKey, isMentorsTable, navigate]);

	const handlePreworkCardClick = useCallback(
		(
			candidateDashboardCardFilter: PreworkDashboardFilter,
			cardIndex: number
		) => {
			const updatedCandidatesFilter = { ...candidatesFilter };
			updatedCandidatesFilter.preworkDashboardFilter =
				candidateDashboardCardFilter;
			updatedCandidatesFilter.preworkDashboardFilterEnabled = false;
			if (cardIndex !== 0) {
				updatedCandidatesFilter.preworkDashboardFilterEnabled = true;
			}
			onCandidateFilterChange(updatedCandidatesFilter);
		},
		[candidatesFilter, onCandidateFilterChange]
	);

	const columns = CandidateTableColumnsPreWork(undefined, viewCandidateMentor);

	const displayButtons = useCallback(() => {
		if (isMentorsTable) {
			return (
				<>
					<Button
						type="primary"
						onClick={handleAssignMentorClick}
						disabled={!hasSelected}
					>
						{t('assignMentor')}
					</Button>
					<Button type="default" onClick={handleBackClick}>
						{t('back')}
					</Button>
				</>
			);
		} else if (
			existingCandidatesIdsForAddMore &&
			existingCandidatesIdsForAddMore.length
		) {
			return (
				<>
					{showAddToGroup && (
						<Button
							type="primary"
							onClick={handleAddToGroupClick}
							disabled={!hasSelected}
						>
							{t('addToGroup')}
						</Button>
					)}
					<Button type="default" onClick={handleBackInAddMoreClick}>
						{t('back')}
					</Button>
					<span className="ml-8">{numberOfCandidates()}</span>
				</>
			);
		} else {
			return (
				<>
					{showCreateGroup && (
						<>
							<Button
								type="primary"
								onClick={handleCreateGroupClick}
								disabled={!hasSelected || isResultPublished}
							>
								{t('createGroup')}
							</Button>
							<span className="ml-8">{numberOfCandidates()}</span>
						</>
					)}
				</>
			);
		}
	}, [
		existingCandidatesIdsForAddMore,
		handleAddToGroupClick,
		handleAssignMentorClick,
		handleBackClick,
		handleBackInAddMoreClick,
		handleCreateGroupClick,
		hasSelected,
		isMentorsTable,
		numberOfCandidates,
		t,
		isResultPublished,
		showCreateGroup,
		showAddToGroup,
	]);

	const buildSortingRequest = (
		sorter: SorterResult<PreworkCandidateSummary>
	): NPHHSortingRequest => {
		const sortRequest: NPHHSortingRequest = {
			sortingFieldName: 'SORTING_FIELD_NAME_UNSPECIFIED',
			sortingOrder: 'SORTING_ORDER_UNSPECIFIED',
		};

		if (!sorter || !sorter.column || !sorter.field) {
			return sortRequest;
		}

		const sortingFieldNameMap: Record<string, string> = {
			city: 'CITY',
			postalCode: 'POSTAL_CODE',
			addressLine1: 'ADDRESS',
			state: 'STATE',
		};

		const sortingOrderMap: Record<string, string> = {
			ascend: 'ASCENDING',
			descend: 'DESCENDING',
		};

		const sortingFieldName =
			(sortingFieldNameMap[sorter.field as string] as NPHHSortingFieldName) ||
			'SORTING_FIELD_NAME_UNSPECIFIED';
		const sortingOrder =
			(sortingOrderMap[sorter.order as string] as SortingOrder) ||
			'SORTING_ORDER_UNSPECIFIED';

		return {
			sortingFieldName,
			sortingOrder,
		};
	};

	const handleTableChange: TableProps<PreworkCandidateSummary>['onChange'] = (
		tablePagination: TablePaginationConfig,
		__: Record<string, FilterValue | null>,
		sorter:
			| SorterResult<PreworkCandidateSummary>
			| SorterResult<PreworkCandidateSummary>[]
	) => {
		if (
			tablePagination.current !== candidatePageConfig.pageNo ||
			tablePagination.pageSize !== candidatePageConfig.pageSize
		) {
			onCandidatesPageChange(
				tablePagination.current ?? 1,
				tablePagination.pageSize ?? DEFAULT_PAGE_SIZE
			);
			return;
		}

		if (!sorter) {
			return;
		}

		const updatedCandidatesFilter = { ...candidatesFilter };
		const sortingRequests: Array<NPHHSortingRequest> = [];

		if (isArray(sorter)) {
			sorter.map((sort) => {
				const sortRequest = buildSortingRequest(sort);
				sortingRequests.push(sortRequest);
			});
		} else {
			const sortRequest = buildSortingRequest(sorter);
			sortingRequests.push(sortRequest);
		}
		updatedCandidatesFilter.sortingRequests = sortingRequests;
		onCandidateFilterChange(updatedCandidatesFilter);
	};

	if (candidates && candidates.length && candidates.length === 0) {
		return <div>{t('noDataFound')}</div>;
	}

	return (
		<>
			{showPreworkCandidates && (
				<FilterCandidatesPreWork
					value={candidatesFilter}
					onFilterChange={onCandidateFilterChange}
					onDashboardDetailFilterChange={onDashboardDetailFilterChange}
					isMentorsFilter={isMentorsTable}
					formName={
						onDashboardDetailFilterChange
							? 'AllPreceptorsFilterForm'
							: 'NewPreceptorsFilterForm'
					}
					activeTabKey={activeTabKey}
					selectedBatchId={selectedBatchId}
				/>
			)}
			{isDashboardTable && (
				<PreworkDetailCardsList
					dashboardDetail={dashboardDetail}
					onCardClick={handlePreworkCardClick}
				/>
			)}{' '}
			<Table
				key={selectedBatchId}
				rowSelection={
					!isDashboardTable && showCreateGroup ? candidateSelection : undefined
				}
				dataSource={candidates}
				columns={columns}
				scroll={{ x: 'max-content' }}
				pagination={tablePaginationConfig}
				onChange={handleTableChange}
			/>
			<div className="flex flex-row-reverse items-center gap-4 my-8">
				{!isDashboardTable && displayButtons()}
			</div>
		</>
	);
};
